// Presentation rules for broker-to-exchange receivables (SM-771). Pure
// functions — the page and the detail panel both read them, and the
// unit tests pin them — so "what may this operator do to this
// receivable right now" is decided in exactly one place.

import { brokerErrorDetail } from "@/components/brokers/governance";
import { brokerRefusalText, RECEIVABLE_RULES } from "@/components/brokers/refusals";
import { APIError } from "@/lib/api";
import type {
  ReceivableActionItem,
  ReceivableActionKind,
  ReceivableActionState,
  ReceivableItem,
  ReceivableResolutionPath,
  ReceivableState,
  SettlementNoteItem,
} from "@/lib/broker-types";
import { intlLocale } from "@/i18n";

// The ledger's canonical(8dp) : public(4dp) ratio (backoffice
// canonPerPublic, ledger publicToCanon).
const CANON_PER_WIRE = 10_000n;

/**
 * A canonical 8dp amount as the 4dp wire integer every money component
 * takes. Truncates toward zero — the sub-4dp tail is below anything the
 * console displays — and keeps the sign. undefined for anything that is
 * not an integer string, so a malformed field renders as a placeholder
 * rather than as a wrong number.
 */
export function canonToWire(canon: string | null | undefined): string | undefined {
  if (canon === null || canon === undefined || !/^-?\d+$/.test(canon.trim())) return undefined;
  return (BigInt(canon.trim()) / CANON_PER_WIRE).toString();
}

export function isUnresolved(state: ReceivableState | string | undefined): boolean {
  return state === "OPEN" || state === "ACKNOWLEDGED";
}

// `label` keys so the page-copy extractor carries every word to the
// Thai catalogue (it reads UI property names, not bare map values).
const STATES: Record<ReceivableState, { label: string; tone: "danger" | "warning" | "success" | "muted" }> = {
  OPEN: { label: "Open", tone: "danger" },
  ACKNOWLEDGED: { label: "Acknowledged", tone: "warning" },
  SETTLED: { label: "Settled", tone: "success" },
  WRITTEN_OFF: { label: "Written off", tone: "muted" },
};

export function receivableStateLabel(state: string): string {
  return STATES[state as ReceivableState]?.label ?? state;
}

export function receivableStateTone(state: string): "danger" | "warning" | "success" | "muted" {
  return STATES[state as ReceivableState]?.tone ?? "muted";
}

const RESOLUTIONS: Record<ReceivableResolutionPath, { label: string }> = {
  COLLATERAL_TOPUP: { label: "Covered by a collateral top-up" },
  NET_POSITION_RECOVERED: { label: "Net position recovered" },
  EXTERNAL_SETTLEMENT: { label: "Settled by the broker" },
  WRITE_OFF: { label: "Written off" },
};

export function resolutionPathLabel(path: string | undefined): string {
  if (!path) return "—";
  return RESOLUTIONS[path as ReceivableResolutionPath]?.label ?? path;
}

const ACTION_KINDS: Record<ReceivableActionKind, { label: string }> = {
  ACKNOWLEDGE: { label: "Acknowledge" },
  SETTLE: { label: "Settlement" },
  WRITE_OFF: { label: "Write-off" },
};

export function actionKindLabel(kind: string): string {
  return ACTION_KINDS[kind as ReceivableActionKind]?.label ?? kind;
}

const ACTION_STATES: Record<ReceivableActionState, { label: string; tone: "warning" | "success" | "muted" }> = {
  PENDING: { label: "Awaiting approval", tone: "warning" },
  APPROVED: { label: "Approved", tone: "success" },
  REJECTED: { label: "Rejected", tone: "muted" },
  APPLIED: { label: "Applied", tone: "success" },
};

export function actionStateLabel(state: string): string {
  return ACTION_STATES[state as ReceivableActionState]?.label ?? state;
}

export function actionStateTone(state: string): "warning" | "success" | "muted" {
  return ACTION_STATES[state as ReceivableActionState]?.tone ?? "muted";
}

/**
 * The settle or write-off waiting for approval, if any —
 * broker-service allows one at a time, so there is at most one; the
 * newest wins should that ever change.
 */
export function pendingAction(r: ReceivableItem): ReceivableActionItem | undefined {
  let newest: ReceivableActionItem | undefined;
  for (const a of r.actions ?? []) {
    if (a.state !== "PENDING" || (a.kind !== "SETTLE" && a.kind !== "WRITE_OFF")) continue;
    if (!newest || a.requested_at_unix_ms >= newest.requested_at_unix_ms) newest = a;
  }
  return newest;
}

/** How long the receivable has been (or was) unresolved. */
export function receivableAgeMs(r: ReceivableItem, nowMs: number): number {
  const end = isUnresolved(r.state) ? nowMs : r.resolved_at_unix_ms || r.updated_at_unix_ms || nowMs;
  return Math.max(0, end - r.opened_at_unix_ms);
}

const HOUR_MS = 3_600_000;

/**
 * The ops alert's horizons (RECEIVABLE_OPEN_AGED, ops-worker): unresolved
 * for more than a day, or still OPEN with nobody owning it after an
 * hour. The console flags the same rows the alert does.
 */
export function isAged(r: ReceivableItem, nowMs: number): boolean {
  if (!isUnresolved(r.state)) return false;
  const age = receivableAgeMs(r, nowMs);
  return age > 24 * HOUR_MS || (r.state === "OPEN" && !r.owner_admin_id && age > HOUR_MS);
}

/**
 * A duration in the two largest units, localised by Intl (en "2d 4h",
 * th "2 วัน 4 ชม."), so the column needs no copy of its own.
 */
export function fmtDuration(ms: number, locale = intlLocale()): string {
  const unit = (value: number, u: "day" | "hour" | "minute") =>
    new Intl.NumberFormat(locale, { style: "unit", unit: u, unitDisplay: "narrow" }).format(value);
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours > 0 ? `${unit(days, "day")} ${unit(hours, "hour")}` : unit(days, "day");
  if (hours > 0) return mins > 0 ? `${unit(hours, "hour")} ${unit(mins, "minute")}` : unit(hours, "hour");
  return unit(mins, "minute");
}

export interface ReceivableAbilities {
  /** OPEN → ACKNOWLEDGED; the acting admin becomes the owner. */
  acknowledge: boolean;
  /** Request a settle / write-off (an admin approves it, the requester included — SM-946). */
  requestResolution: boolean;
  /** The pending action this admin may approve or reject, if any. */
  pending?: ReceivableActionItem;
}

/**
 * What an admin holding (or not holding) BROKER_RECEIVABLE_MANAGE may do
 * to r. The server re-checks every one of these; the console mirrors
 * them so a button that would only be refused is never offered as live.
 */
export function receivableAbilities(r: ReceivableItem, canManage: boolean): ReceivableAbilities {
  const pending = pendingAction(r);
  const open = isUnresolved(r.state);
  return {
    acknowledge: canManage && r.state === "OPEN",
    requestResolution: canManage && open && !pending,
    pending: open ? pending : undefined,
  };
}

/** A unix-ms instant as an ISO string, for the shared date formatters. */
export function msToIso(ms: number | undefined): string | undefined {
  return ms ? new Date(ms).toISOString() : undefined;
}

// ── settling from a recorded payment (SM-948) ─────────────────────────
//
// PO 2026-10-19: a bank payment that covers a receivable is recorded once,
// as a settlement note, and that note then settles the receivable. The
// note must be CONFIRMED and INBOUND, recorded on the receivable's root
// itself in the same currency, not paying a payment request and not used
// by another live settle; one smaller than the receivable is refused
// (NOTE_TOO_SMALL), one larger settles it at the receivable's amount and
// the rest is not used for anything else. broker-service re-checks every
// rule; the console mirrors them so a payment that would only be refused
// is never offered as live.

/** A canonical 8dp amount as words an operator reads ("1,000.5 GOLD"). */
export function canonAmountText(canon: string | undefined, currency: string, locale = intlLocale()): string {
  if (!canon || !/^-?\d+$/.test(canon.trim())) return "—";
  let v = BigInt(canon.trim());
  const neg = v < 0n;
  if (neg) v = -v;
  const whole = v / 100_000_000n;
  const frac = (v % 100_000_000n).toString().padStart(8, "0").slice(0, 4).replace(/0+$/, "");
  const grouped = new Intl.NumberFormat(locale).format(whole);
  const sep = new Intl.NumberFormat(locale).format(1.5).charAt(1) || ".";
  return `${neg ? "-" : ""}${grouped}${frac ? `${sep}${frac}` : ""} ${currency}`;
}

/** One confirmed payment the Record settlement dialog offers. */
export interface SettleCandidate {
  note: SettlementNoteItem;
  /** Smaller than the receivable: shown, never selectable. */
  tooSmall: boolean;
  /** note − receivable (canonical 8dp, may be negative). */
  difference: bigint;
}

function canonBig(v: string | undefined): bigint | null {
  return v && /^\d+$/.test(v.trim()) ? BigInt(v.trim()) : null;
}

/**
 * The notes a settle of r may name, newest value date first: CONFIRMED,
 * INBOUND, recorded on r's root itself, in r's currency, paying no
 * payment request and used by no live settle. A note smaller than r stays
 * in the list (disabled, with a hint) so the operator sees why it can't
 * be picked; every other exclusion is dropped.
 */
export function settleCandidates(notes: SettlementNoteItem[], r: ReceivableItem): SettleCandidate[] {
  const owed = canonBig(r.amount) ?? 0n;
  const out: SettleCandidate[] = [];
  for (const n of notes) {
    if (n.status !== "CONFIRMED" || n.direction !== "INBOUND") continue;
    if (n.broker_id !== r.broker_id || n.currency !== r.currency) continue;
    if (n.payment_request_id || n.settles_receivable_id || n.superseded_by_note_id) continue;
    const amount = canonBig(n.amount);
    if (amount === null) continue;
    out.push({ note: n, tooSmall: amount < owed, difference: amount - owed });
  }
  return out.sort((a, b) =>
    a.note.value_date === b.note.value_date
      ? b.note.created_at_unix_ms - a.note.created_at_unix_ms
      : a.note.value_date < b.note.value_date
        ? 1
        : -1,
  );
}

/** The candidate rows' hint, in plain words; none when the note matches exactly. */
export function settleCandidateHint(c: SettleCandidate, r: ReceivableItem): string | undefined {
  if (c.tooSmall) {
    const hint = `Smaller than the ${canonAmountText(r.amount, r.currency)} owed — this payment can't settle it.`;
    return hint;
  }
  if (c.difference > 0n) {
    const hint = `${canonAmountText(c.difference.toString(), r.currency)} more than the receivable. The rest isn't used here.`;
    return hint;
  }
  return undefined;
}

const NOTE_NOT_FOUND = { message: "That payment record wasn't found. Pick another payment, or enter a payment reference." };

/**
 * A receivable refusal in plain words. NOTE_TOO_SMALL names both amounts;
 * at approval (the receivable grew after the request) it says to reject
 * and use a larger payment. Anything else is worded by refusals.ts.
 */
export function receivableRefusalText(e: unknown, currency: string, stage: "request" | "approve"): string | null {
  if (!e) return null;
  if (e instanceof APIError) {
    const d = brokerErrorDetail(e);
    const rule = d?.details?.rule;
    if (e.code === "BROKER_RECEIVABLE_TRANSITION_INVALID" && rule === "NOTE_TOO_SMALL") {
      const paid = canonAmountText(d?.details?.note_amount, currency);
      const owed = canonAmountText(d?.details?.receivable_amount, currency);
      if (stage === "approve") {
        const message = `This payment (${paid}) is smaller than what the broker owes (${owed}). Reject this settlement and use a larger payment.`;
        return message;
      }
      const message = `This payment (${paid}) is smaller than what the broker owes (${owed}). Choose a larger payment.`;
      return message;
    }
    if (e.code === "BROKER_RECEIVABLE_TRANSITION_INVALID" && typeof rule === "string" && RECEIVABLE_RULES[rule]) {
      return RECEIVABLE_RULES[rule].message;
    }
    if (e.status === 404 && /settlement note/i.test(e.message)) return NOTE_NOT_FOUND.message;
  }
  return brokerRefusalText(e);
}

